import axios from "axios";
import appConfig from "../config/appConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { rulesApiRequest, apiScopes } from "../config/msalConfig";
import store from '../redux/store';
import packageJson from '../../package.json';

const BASE_URL = `${appConfig.apiUrl}rules/`;
const CACHE_URL = `${appConfig.cacheUrl}api/CacheTool/`;
const NPI_URL = `${appConfig.npiUrl}api/`;

let msalInstance = null;
let userAccounts = [];

export const initializeAuth = async (instance, accounts) => {
  msalInstance = instance;
  userAccounts = accounts;
};

export const getAccessToken = async (request = rulesApiRequest) => {
  if (!msalInstance) {
    console.error("MSAL instance is not available");
    return null;
  }

  if (!userAccounts || userAccounts.length === 0) {
    console.error("No accounts available");
    return null;
  }

  const account = userAccounts[0];

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...request,
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup({
          ...request,
          account: account,
        });
        return response.accessToken;
      } catch (popupError) {
        console.error("Token acquisition via popup failed:", popupError);
        return null;
      }
    } else {
      console.error("Silent token acquisition failed:", error);
      return null;
    }
  }
};

export const fetchSecure = async (accessToken, apiEndpoint, options = {}) => {
  if (!accessToken) {
    console.error("Access token is not available");
    return null;
  }

  try {
    const response = await axios({
      method: options.method || "GET",
      url: apiEndpoint,
                headers: {
        Authorization: `Bearer ${accessToken}`,
        'Database-Source': getDbSrc(),
        ...options.headers,
      },
      data: options.data || null,
      params: options.params || null,
      responseType: options.responseType || "json",
            });
    return response.data;
  } catch (error) {
    console.error("API call failed:", error);
    if (options.throwOnError) {
      throw error;
    }
    return null;
  }
};

const getDbSrc = () => {
  const reduxStore = store.getState();
  return reduxStore.rules.dbSrc;
}

export const getSearchResults = async (searchText, domain) => {
  console.log("Fetching tree...");
  const token = await getAccessToken();
  return await fetchSecure(token, `${BASE_URL}tree?searchText=${searchText}&domain=${domain}`);
};

export const getTreeById = async (rulesetGroupId) => {
  console.log("Fetching tree...");
  const token = await getAccessToken();
  return await fetchSecure(token, `${BASE_URL}treebyid?rulesetGroupId=${rulesetGroupId}`);
};

export const getRuleById = async (ruleId) => {
  const accessToken = await getAccessToken();
  const url = `${BASE_URL}byid?ruleId=${ruleId}`;
  const rules = await fetchSecure(accessToken, url);
  return rules || {};      
};

export const getMatrices = async () => {
  const accessToken = await getAccessToken();
  const url = `${BASE_URL}matrices`;
  const rules = await fetchSecure(accessToken, url);
  return rules || {};      
};

export const getMatrixData = async (matrix, { signal } = {}) => {
  if (!matrix || !matrix.trim()) throw new Error("Matrix name is required.");

  const accessToken = await getAccessToken();
  const url = `${BASE_URL}matrix-data/${encodeURIComponent(matrix.trim())}`;
  const data = await fetchSecure(accessToken, url, { signal });
  return Array.isArray(data) ? data : (data ?? []);
};

export const getRelatedParts = async (partNumber, searchType) => {
  if (!partNumber || !partNumber.trim()) throw new Error("Part Number is required.");
  const url = `${CACHE_URL}GetRelatedParts/`;
  const options = {
    method: 'POST',
    data: { partNumber: partNumber, searchType: searchType},
    throwOnError: true,
  };

  const accessToken = await getAccessToken({ scopes: apiScopes });
  if (!accessToken) {
    throw new Error("Unable to acquire a CacheTool access token. Please sign in again.");
  }

  const start = Date.now();
  const partsResponse = await fetchSecure(accessToken, url, options);
  if (!Array.isArray(partsResponse)) {
    throw new Error("CacheTool returned an invalid related parts response.");
  }
  const timeToRun = Math.round((Date.now() - start) / 1000);
  SendPostRequest("CacheViewer", timeToRun, 0, 0);

  return partsResponse;
};

export const SendPostRequest = async (appExecuted, timeToRun, numberOfErrors = 0, numberOfWarning = 0) => {
  const url = `${NPI_URL}PostFunction`;

  const username = userAccounts[0]?.name || "Unknown User";
  const jsonObject = 
  {
      AppExecuted: appExecuted,
      MachineName: "React Client",
      NumberOfErrors: numberOfErrors,
      NumberOfWarnings: numberOfWarning,
      TimeToRunApp: timeToRun,
      UserName: username,
      Version: packageJson.version
  };

  const options = {
    method: 'POST',
    headers: {
      'x-functions-key': 'OvrArPWivmrU5RytrJLmZxuk_9xSr2WE16uM5QoGkS0ZAzFudJJhdw==',
    },
    data: jsonObject
  };

  try {
    const response = await axios({
      method: options.method || "POST",
      url: url,
      headers: {...options.headers},
      data: options.data || null,
      responseType: options.responseType || "json",
            });
    return response.data;
  } catch (error) {
    console.error("API call failed:", error);
    return null;
  }
};
